import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Check, ChevronRight, Copy, Plus } from 'lucide-react';
import {
  Banner, Button, EmptyRow, MobileList, MobileListEmpty, MobileListSkeleton, MobileRow, SearchField, SkeletonRows,
} from '../kit';
import { useIsMobile } from '../../../hooks/use-mobile';
import { fetchLocationsForOrg } from '../../../utils/locationApi';
import { apiErrorMessage } from '../../../utils/organizationApi';
import { cityLine, DEFAULT_LOCATION_TIMEZONE, linkedSystemFlags } from './orgModel';
import { LinkedBadges } from './orgsUi';
import LocationPanel from './LocationPanel';

const COLUMNS = 6;

// Inbound report address — the Gmail catch-all routes reports_<id>@ to the
// ETL inbox, which reads the location id back out of the address.
const reportsEmail = (id) => `reports_${id}@lodgeic.io`;

function CopyEmail({ value }) {
  const [copied, setCopied] = useState(false);
  const copy = async (e) => {
    e.stopPropagation(); // the row opens the location panel
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (_) {
      window.prompt('Copy the address:', value); // clipboard blocked (http / permissions)
    }
  };
  const Icon = copied ? Check : Copy;
  return (
    <span className="org-email">
      <span className="ak-mono">{value}</span>
      <button
        type="button"
        className={`org-email-copy${copied ? ' is-copied' : ''}`}
        onClick={copy}
        onKeyDown={(e) => e.stopPropagation()}
        aria-label={copied ? 'Copied' : `Copy ${value}`}
        title={copied ? 'Copied' : 'Copy email'}
      >
        <Icon size={14} aria-hidden />
      </button>
    </span>
  );
}

/** Phones: one card per location — name, city, then timezone · currency. */
function LocationCards({ locations, visible, loading, error, query, org, selectedId, onOpen, onAdd, onClearSearch }) {
  let empty = null;
  if (!loading && !error && locations.length === 0) {
    empty = {
      title: 'No locations yet',
      body: `Add ${org.name}'s first property to start loading its data.`,
      action: <Button variant="primary" icon={Plus} onClick={onAdd}>Add location</Button>,
    };
  } else if (!loading && locations.length > 0 && visible.length === 0) {
    empty = { title: 'No matches', body: `No location matches “${query}”.`, action: <Button onClick={onClearSearch}>Clear search</Button> };
  }
  return (
    <MobileList label="Locations">
      {loading ? <MobileListSkeleton rows={6} lead={false} /> : null}
      {empty ? <MobileListEmpty title={empty.title} body={empty.body} action={empty.action} /> : null}
      {!loading && visible.map((l) => {
        const city = cityLine(l);
        return (
          <MobileRow
            key={l.id}
            selected={selectedId === l.id}
            onClick={() => onOpen(l)}
            title={l.name || 'Unnamed location'}
            sub={(
              <span className="ak-m-shrink">
                {city ? `${city} · ` : ''}
                <span className="ak-mono">{l.timezone || `${DEFAULT_LOCATION_TIMEZONE}*`}</span>
                {' · '}
                <span className="ak-mono">{l.currency || 'USD*'}</span>
              </span>
            )}
            aside={<ChevronRight className="ak-m-chevron" aria-hidden="true" />}
          />
        );
      })}
    </MobileList>
  );
}

export default function OrgLocationsTab({ org, setActions, onChanged }) {
  const isMobile = useIsMobile();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [panel, setPanel] = useState(null); // { location } | { location: null } for new

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const rows = await fetchLocationsForOrg(org.id);
      setLocations(rows);
      setError(null);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not load locations.'));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [org.id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    setActions(<Button variant="primary" icon={Plus} onClick={() => setPanel({ location: null })}>Add location</Button>);
  }, [setActions]);
  useEffect(() => () => setActions(null), [setActions]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return locations;
    return locations.filter((l) => [
      l.name, l.code, String(l.id), l.city, l.state, l.timezone, l.currency,
      ...linkedSystemFlags(l).map((f) => f.value),
    ].some((v) => String(v || '').toLowerCase().includes(q)));
  }, [locations, query]);

  const afterSave = async () => {
    await load({ silent: true });
    onChanged();
  };

  return (
    <>
      {error ? (
        <Banner tone="bad" icon={AlertTriangle} title="Couldn't load locations" action={<Button onClick={() => load()}>Try again</Button>}>
          {error}
        </Banner>
      ) : null}
      <div className="ak-frame">
        <div className={`ak-toolbar${isMobile ? ' ak-m-toolbar' : ''}`}>
          <SearchField value={query} onChange={setQuery} placeholder="Search name, code, city or ID" label="Search locations" />
          <span className="ak-toolbar-meta">
            {loading ? ' ' : `${visible.length === locations.length ? locations.length : `${visible.length} of ${locations.length}`} location${locations.length === 1 ? '' : 's'}`}
          </span>
        </div>
        {isMobile ? (
          <LocationCards
            locations={locations}
            visible={visible}
            loading={loading}
            error={error}
            query={query}
            org={org}
            selectedId={panel?.location?.id}
            onOpen={(l) => setPanel({ location: l })}
            onAdd={() => setPanel({ location: null })}
            onClearSearch={() => setQuery('')}
          />
        ) : (
          <div className="ak-scroll">
            <table className="ak-tbl orgs-tbl">
              <thead>
                <tr>
                  <th scope="col">Location</th>
                  <th scope="col">City</th>
                  <th scope="col">Property email</th>
                  <th scope="col">Timezone</th>
                  <th scope="col">Currency</th>
                  <th scope="col">Linked systems</th>
                </tr>
              </thead>
              <tbody>
                {loading ? <SkeletonRows rows={6} columns={COLUMNS} /> : null}
                {!loading && !error && locations.length === 0 ? (
                  <EmptyRow
                    colSpan={COLUMNS}
                    title="No locations yet"
                    body={`Add ${org.name}'s first property to start loading its data.`}
                    action={<Button variant="primary" icon={Plus} onClick={() => setPanel({ location: null })}>Add location</Button>}
                  />
                ) : null}
                {!loading && locations.length > 0 && visible.length === 0 ? (
                  <EmptyRow colSpan={COLUMNS} title="No matches" body={`No location matches “${query}”.`} action={<Button onClick={() => setQuery('')}>Clear search</Button>} />
                ) : null}
                {!loading && visible.map((l) => (
                  <tr
                    key={l.id}
                    className={`ak-click${panel?.location?.id === l.id ? ' ak-selected' : ''}`}
                    tabIndex={0}
                    aria-label={`Edit ${l.name || `location ${l.id}`}`}
                    onClick={() => setPanel({ location: l })}
                    onKeyDown={(e) => { if (e.key === 'Enter') setPanel({ location: l }); }}
                  >
                    <td>
                      <div className="ak-cell-id">
                        <div>
                          <span className="ak-cell-title">{l.name || 'Unnamed location'}</span>
                          <span className="ak-mono ak-muted">{l.code || '—'} · #{l.id}</span>
                        </div>
                      </div>
                    </td>
                    <td>{cityLine(l) || <span className="ak-muted">—</span>}</td>
                    <td><CopyEmail value={reportsEmail(l.id)} /></td>
                    <td className="ak-mono">{l.timezone || <span className="ak-muted" title="No timezone set — America/Chicago is used">{DEFAULT_LOCATION_TIMEZONE}*</span>}</td>
                    <td className="ak-mono">{l.currency || <span className="ak-muted" title="No currency set — USD is used">USD*</span>}</td>
                    <td><LinkedBadges location={l} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <LocationPanel
        open={Boolean(panel)}
        org={org}
        location={panel?.location || null}
        onClose={() => setPanel(null)}
        onSaved={async () => { setPanel(null); await afterSave(); }}
        onDeleted={async () => { setPanel(null); await afterSave(); }}
      />
    </>
  );
}
